/**
 * Native Push Notification Service
 *
 * Handles native push notifications for iOS and Android using Firebase Messaging.
 * Falls back gracefully on web platforms.
 */

import { Capacitor } from '@capacitor/core';
import { FirebaseMessaging, GetTokenOptions } from '@capacitor-firebase/messaging';
import { supabase } from '@/integrations/supabase/client';

export interface PushNotificationData {
  type?: string;
  game_id?: string;
  round_id?: string;
  club_id?: string;
  conversation_id?: string;
  tournament_id?: string;
  user_id?: string;
  [key: string]: string | undefined;
}

/**
 * Security: Allowed notification types for deep linking
 * Only these types can trigger navigation
 */
const ALLOWED_NOTIFICATION_TYPES = new Set([
  'game_invite',
  'round_completed',
  'club_announcement',
  'friend_request',
  'chat_message',
  'badge_earned',
  'achievement',
  'tournament_reminder',
  'tournament_update',
  'coaching_session',
  'coaching_booked',
  'coaching_cancelled',
]);

/**
 * Security: Validate that an ID is safe for use in URLs
 * Must be alphanumeric with optional hyphens/underscores (UUID format)
 * Prevents path traversal attacks like "../admin" or "123/../../secrets"
 */
function isValidId(id: string | undefined): id is string {
  if (!id || typeof id !== 'string') return false;
  // Allow UUIDs, numeric IDs, and alphanumeric slugs
  // Block: path traversal (..), query strings (?), fragments (#), slashes (/)
  const SAFE_ID_PATTERN = /^[a-zA-Z0-9_-]+$/;
  return SAFE_ID_PATTERN.test(id) && id.length <= 128;
}

/**
 * Security: Safe navigation that only allows whitelisted paths
 */
function safeNavigate(path: string): void {
  // Double-check the path is safe before navigation
  if (!path.startsWith('/') || path.includes('..') || path.includes('//')) {
    console.warn('[NativePush] Blocked unsafe navigation path:', path);
    window.location.href = '/notifications';
    return;
  }
  window.location.href = path;
}

class NativePushService {
  private static instance: NativePushService;
  private initialized = false;
  private currentToken: string | null = null;

  static getInstance(): NativePushService {
    if (!this.instance) {
      this.instance = new NativePushService();
    }
    return this.instance;
  }

  /**
   * Check if we're on a native platform that supports push
   */
  isSupported(): boolean {
    return Capacitor.isNativePlatform();
  }

  /**
   * Initialize push notifications
   * Call this after user is authenticated
   */
  public lastError: string | null = null;

  async initialize(userId?: string): Promise<boolean> {
    this.lastError = null;

    if (!this.isSupported()) {
      this.lastError = 'Not a native platform';
      console.log('[NativePush] Not a native platform, skipping initialization');
      return false;
    }

    try {
      // Check current permission status
      const permStatus = await FirebaseMessaging.checkPermissions();
      console.log('[NativePush] Permission status:', permStatus.receive);

      if (permStatus.receive === 'prompt') {
        // Request permission
        const result = await FirebaseMessaging.requestPermissions();
        if (result.receive !== 'granted') {
          this.lastError = `Permission denied: ${result.receive}`;
          console.log('[NativePush] Permission denied by user');
          return false;
        }
      } else if (permStatus.receive !== 'granted') {
        this.lastError = `Permission not granted: ${permStatus.receive}`;
        console.log('[NativePush] Permission not granted:', permStatus.receive);
        return false;
      }

      // Set up listeners
      this.setupListeners();

      // Get FCM token
      const tokenResult = await FirebaseMessaging.getToken();
      if (tokenResult.token) {
        console.log('[NativePush] Got FCM token:', tokenResult.token.substring(0, 20) + '...');
        this.currentToken = tokenResult.token;
        await this.saveDeviceToken(tokenResult.token, userId);
      } else {
        this.lastError = 'getToken returned empty token';
        console.error('[NativePush] getToken returned no token');
      }

      this.initialized = true;
      console.log('[NativePush] Initialization complete');
      return true;
    } catch (error) {
      this.lastError = `Exception: ${error instanceof Error ? error.message : String(error)}`;
      console.error('[NativePush] Initialization failed:', error);
      return false;
    }
  }

  private listenersSetUp = false;

  /**
   * Set up push notification event listeners (idempotent — only runs once)
   */
  private setupListeners(): void {
    if (this.listenersSetUp) return;
    this.listenersSetUp = true;
    // Token refresh
    FirebaseMessaging.addListener('tokenReceived', async (event) => {
      console.log('[NativePush] Token refreshed:', event.token.substring(0, 20) + '...');
      this.currentToken = event.token;
      await this.saveDeviceToken(event.token);
    });

    // Push received while app is in foreground
    FirebaseMessaging.addListener('notificationReceived', (event) => {
      console.log('[NativePush] Received in foreground:', event.notification.title);
      this.handleForegroundNotification(event.notification);
    });

    // User tapped on notification
    FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
      console.log('[NativePush] Action performed:', event.actionId);
      this.handleNotificationTap(event);
    });
  }

  /**
   * Handle notification received while app is in foreground
   */
  private handleForegroundNotification(notification: { title?: string; body?: string; data?: Record<string, unknown> }): void {
    console.log('[NativePush] Foreground notification:', {
      title: notification.title,
      body: notification.body,
      data: notification.data,
    });
    window.dispatchEvent(new Event('push-notification-received'));
  }

  /**
   * Handle user tapping on a notification - navigate to relevant screen
   *
   * SECURITY: All IDs are validated before use in URLs to prevent:
   * - Path traversal attacks (e.g., game_id: "../admin")
   * - Query string injection (e.g., game_id: "123?admin=true")
   * - Only whitelisted notification types are processed
   */
  private handleNotificationTap(action: { actionId: string; notification: { data?: Record<string, unknown> } }): void {
    const data = action.notification.data as PushNotificationData | undefined;

    // Security: Validate notification type is in whitelist
    if (!data?.type || !ALLOWED_NOTIFICATION_TYPES.has(data.type)) {
      console.warn('[NativePush] Unknown or blocked notification type:', data?.type);
      safeNavigate('/notifications');
      return;
    }

    // Deep link based on notification type with validated IDs
    switch (data.type) {
      case 'game_invite':
        if (isValidId(data.game_id)) {
          safeNavigate(`/game/${data.game_id}`);
        } else {
          safeNavigate('/notifications');
        }
        break;

      case 'round_completed':
        if (isValidId(data.round_id)) {
          safeNavigate(`/round/${data.round_id}/summary`);
        } else {
          safeNavigate('/notifications');
        }
        break;

      case 'club_announcement':
        if (isValidId(data.club_id)) {
          safeNavigate(`/club/${data.club_id}/announcements`);
        } else {
          safeNavigate('/notifications');
        }
        break;

      case 'friend_request':
        safeNavigate('/friends');
        break;

      case 'chat_message':
        if (isValidId(data.conversation_id)) {
          safeNavigate(`/messages/${data.conversation_id}`);
        } else {
          safeNavigate('/messages');
        }
        break;

      case 'badge_earned':
      case 'achievement':
        safeNavigate('/profile/badges');
        break;

      case 'tournament_reminder':
      case 'tournament_update':
        if (isValidId(data.tournament_id)) {
          safeNavigate(`/tournament/${data.tournament_id}`);
        } else {
          safeNavigate('/notifications');
        }
        break;

      case 'coaching_session':
      case 'coaching_booked':
      case 'coaching_cancelled':
        safeNavigate('/coaching');
        break;

      default:
        // This shouldn't happen due to whitelist check above, but defense in depth
        safeNavigate('/notifications');
    }
  }

  /**
   * Save device token to Supabase
   * userId is passed directly from the hook to avoid a PKCE-verified getUser() network call
   */
  private async saveDeviceToken(token: string, userId?: string): Promise<void> {
    try {
      let uid = userId;
      if (!uid) {
        // Fallback: fetch from session (may fail on Capacitor with PKCE if session not ready)
        const { data: { user } } = await supabase.auth.getUser();
        uid = user?.id;
      }

      if (!uid) {
        console.log('[NativePush] No user logged in, cannot save token');
        return;
      }

      const platform = Capacitor.getPlatform() as 'ios' | 'android';

      const deviceData = {
        user_id: uid,
        platform,
        token,
        device_name: `${platform} device`,
        device_model: platform,
        os_version: platform,
        app_version: '1.0.0',
        is_active: true,
        last_used_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('device_tokens')
        .upsert(deviceData, {
          onConflict: 'user_id,token',
        });

      if (error) {
        console.error('[NativePush] Failed to save device token:', error);
      } else {
        console.log('[NativePush] Device token saved successfully');
      }
    } catch (error) {
      console.error('[NativePush] Error saving device token:', error);
    }
  }

  /**
   * Remove/deactivate device token (call on logout)
   */
  async removeDeviceToken(): Promise<void> {
    if (!this.isSupported() || !this.currentToken) {
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Delete the token from Firebase
      await FirebaseMessaging.deleteToken();

      const { error } = await supabase
        .from('device_tokens')
        .update({ is_active: false, updated_at: new Date().toISOString() })
        .eq('user_id', user.id)
        .eq('token', this.currentToken);

      if (error) {
        console.error('[NativePush] Failed to deactivate device token:', error);
      } else {
        console.log('[NativePush] Device token deactivated');
      }

      this.currentToken = null;
    } catch (error) {
      console.error('[NativePush] Error removing device token:', error);
    }
  }

  /**
   * Get current permission status
   */
  async getPermissionStatus(): Promise<'granted' | 'denied' | 'prompt' | 'unsupported'> {
    if (!this.isSupported()) {
      return 'unsupported';
    }

    try {
      const status = await FirebaseMessaging.checkPermissions();
      return status.receive as 'granted' | 'denied' | 'prompt';
    } catch {
      return 'unsupported';
    }
  }

  /**
   * Request permission explicitly (for settings page)
   */
  async requestPermission(): Promise<boolean> {
    if (!this.isSupported()) {
      return false;
    }

    try {
      const result = await FirebaseMessaging.requestPermissions();
      if (result.receive === 'granted') {
        const tokenResult = await FirebaseMessaging.getToken();
        if (tokenResult.token) {
          this.currentToken = tokenResult.token;
          await this.saveDeviceToken(tokenResult.token);
        }
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }
}

export const nativePushService = NativePushService.getInstance();
